import {
  defaults,
  isEmpty,
  head,
  get,
  isEqual,
  omitBy,
  merge,
  omit,
} from 'lodash'
import { match } from 'ts-pattern'
import * as UNDO_REDO_LABELS from '@wix/wix-data-client-common/dist/esm/undoRedoLabels'
import {
  isSinglesCollectionSchema,
  serializeSort,
  deserializeSort,
  getDefaultPageSize,
  WIDGET_DATASET,
  TransactionClient,
  ALL_DATASET_TYPES,
  ROUTER_DATASET,
  DATASET,
} from '@wix/wix-data-client-common'
import {
  generateUniqueName,
  TEXT_ROLE,
  GRID_ROLE,
} from '@wix/wix-data-client-common-standalone'
import { defaultDatasetConfiguration } from '@wix/wix-data-client-common/dist/esm/dataset-configuration/defaults'
import {
  serializeFilter,
  deserializeFilter,
  extractFilterAndConnection,
} from '../../business-logic/filter/transform'
import {
  saveRouterDatasetConfig,
  getRouterDatasetConfig,
} from '../../business-logic/datasets/routerDataset'
import { getDatasetComponentDefinition } from '../../business-logic/datasets/datasetComponentDefinition'
import { fetchExistingControllerNames } from '../../business-logic/datasets/datasetDisplayName'
import { getRepeaterAncestor } from '../../business-logic/components/getRepeaterAncestor'
import { getRepeaterItemAncestor } from '../../business-logic/components/getRepeaterItemAncestor'
import syncPropsDerivedFromConnections from '../../business-logic/datasets/syncPropsDerivedFromConnections'
import {
  checkIfComponentHasNoOtherPrimaryConnection,
  clearAllControllerConnections,
  connectRepeaterAncestorIfNeeded,
  fetchCollectionSchemas,
} from './utils'
import {
  markComponentNonRequired,
  markComponentNonReadOnly,
  uncheckComponentsCheckedProperty,
  updateComponentLabelAndPlaceholder,
  overrideRangeStepsDefaults,
} from './userFilterComponentOverrides'
import {
  fetchFilterConnections,
  syncFilterConnections,
} from './filterConnections'
import {
  clearComponentBehaviorMetadata,
  syncComponentBehaviorMetadata,
} from './componentBehaviorMetadata'
import { createConnectionConfig, disconnectBinding } from './bindings'
import { parseBindings } from './parseBindings'
import { getSubrole } from './subroles'
import { addSubmitFeedbackMessage } from './addSubmitFeedbackMessage'

const toApiConfig = (datasetConfig, filterConnections) => {
  const datasetConfigWithDefaults = defaults(
    {},
    datasetConfig,
    defaultDatasetConfiguration,
  )
  return {
    collectionId: datasetConfigWithDefaults.collectionName,
    includes: datasetConfigWithDefaults.includes,
    readWriteMode: datasetConfigWithDefaults.readWriteType,
    pageSize: datasetConfigWithDefaults.pageSize,
    deferred: Boolean(datasetConfigWithDefaults.deferred),
    cursor: Boolean(datasetConfigWithDefaults.cursor),
    onlyUsedFields: Boolean(datasetConfigWithDefaults.onlyUsedFields),
    private: Boolean(datasetConfigWithDefaults.private),
    sorts: datasetConfigWithDefaults.sort
      ? deserializeSort(datasetConfigWithDefaults.sort)
      : [],
    filters: deserializeFilter(
      datasetConfigWithDefaults.filter,
      filterConnections,
    ),
    nested: datasetConfigWithDefaults.nested,
    randomSort: Boolean(datasetConfigWithDefaults.randomSort),
  }
}

const fromApiConfig = (apiDatasetConfig, controllerRef) => {
  const { filter, connections: filterConnections } = fromApiFilters(
    apiDatasetConfig.filters,
    controllerRef,
  )
  return {
    datasetConfig: {
      collectionName: apiDatasetConfig.collectionId,
      includes: apiDatasetConfig.includes,
      readWriteType: apiDatasetConfig.readWriteMode,
      pageSize: apiDatasetConfig.pageSize,
      deferred: apiDatasetConfig.deferred,
      cursor: apiDatasetConfig.cursor,
      onlyUsedFields: apiDatasetConfig.onlyUsedFields,
      private: apiDatasetConfig.private,
      sort: isEmpty(apiDatasetConfig.sorts)
        ? apiDatasetConfig.sorts && null
        : serializeSort(...apiDatasetConfig.sorts),
      filter: apiDatasetConfig.filters && filter,
      nested: apiDatasetConfig.nested,
      randomSort: apiDatasetConfig.randomSort,
    },
    filterConnections,
  }
}

const getCollectionIdFromConfig = controllerConfig =>
  get(controllerConfig, 'dataset.collectionName', null)

const datasetPropertiesToOmitIfFalse = [
  'deferred',
  'cursor',
  'onlyUsedFields',
  'private',
  'randomSort',
]

const omitFalseProperties = datasetConfig =>
  omitBy(
    datasetConfig,
    (value, key) =>
      datasetPropertiesToOmitIfFalse.includes(key) && value === false,
  )
const fromApiFilters = (apiFilters, controllerRef) => {
  if (isEmpty(apiFilters)) {
    return {
      filter: null,
      connections: [],
    }
  }

  const { filters, connections } = apiFilters
    .map(filter => extractFilterAndConnection(filter, controllerRef))
    .reduce(
      (result, current) => ({
        ...result,
        filters: result.filters.concat(current.filters),
        connections: result.connections.concat(current.connections),
      }),
      { filters: [], connections: [] },
    )

  return {
    connections,
    filter: serializeFilter(...filters),
  }
}

const controllerConnectionChanged = async ({
  editorSdkProxy,
  controllerRef,
}) => {
  const appApi = await editorSdkProxy.editor.getAppAPI()
  await appApi.emit('controllerConnectionChanged', {
    controllerRef,
  })
}

/**
 * DatasetConfig type definition
 * @typedef {Object} DatasetConfig
 * @property {string} collectionId
 * @property {string[]} includes
 * @property {string} readWriteType
 * @property {number} pageSize
 * @property {boolean} deferred
 * @property {boolean} onlyUsedFields
 * @property {boolean} private
 * @property {filterExpression[]} filters
 * @property {sortExpression[]} sorts
 */

/**
 * ComponentRef type definition
 * @typedef {Object} ComponentRef
 * @property {string} id
 * @property {string} type
 * /

/**
 * DatasetInfo type definition
 * @typedef {Object} DatasetInfo
 * @property {ComponentRef} controllerRef
 * @property {string} controllerType
 * @property {string} displayName
 * @property {DatasetConfig} config
 */

export default function DataBindingApi({
  i18n,
  editorSdkProxy,
  collectionsApi,
  isTransactionFlowEnabled,
  editorType,
  features,
}) {
  const transactionClient = TransactionClient({
    editorSdkProxy,
    useTransactions: isTransactionFlowEnabled,
  })

  /**
   * Update a Dataset's collection without clearing connections. Should only be used for presets.
   * Only regular datasets are supported.
   * @param {Object} params
   * @param {ComponentRef} params.controllerRef - Reference to the dataset controller.
   * @param {string} params.collectionId - The ID of the collection to set.
   * @param {string} params.collectionName - The display name of the collection.
   * @returns {Promise<void>}
   */
  const changePresetDatasetCollection = async ({
    controllerRef,
    collectionId,
    collectionName,
  }) => {
    const { type: controllerType, config: existingControllerConfig } =
      await editorSdkProxy.controllers.getData({ controllerRef })

    if (controllerType !== DATASET) {
      throw new Error(`Unsupported controller type ${controllerType}`)
    }

    const updatedDataset = {
      ...existingControllerConfig.dataset,
      collectionName: collectionId,
    }

    const datasetDisplayName = `${collectionName} dataset`

    return Promise.all([
      setDatasetDisplayName(controllerRef, datasetDisplayName, true),
      editorSdkProxy.controllers.saveConfiguration({
        controllerRef,
        config: {
          dataset: omitFalseProperties(updatedDataset),
        },
      }),
    ])
  }

  /**
   * Update a Dataset or RouterDataset's config.
   * Any config property that is not passed, will not be updated.
   * @param {*} controllerRef
   * @param {DatasetConfig} apiConfig
   */
  const updateDatasetConfig = async (
    controllerRef,
    apiConfig,
    withHistoryLabel = true,
  ) => {
    const { type: controllerType, config: controllerConfig } =
      await editorSdkProxy.controllers.getData({ controllerRef })

    assertValidDatasetType(controllerType)

    const existingControllerConfig = await match(controllerType)
      .with(DATASET, WIDGET_DATASET, () => controllerConfig)
      .with(ROUTER_DATASET, () =>
        getRouterDatasetConfig(editorSdkProxy, controllerRef, controllerConfig),
      )
      .exhaustive()

    const {
      datasetConfig: datasetConfigUpdates,
      filterConnections: filterConnectionsUpdates,
    } = fromApiConfig(apiConfig, controllerRef)

    const wasCollectionChanged =
      datasetConfigUpdates.collectionName &&
      getCollectionIdFromConfig(existingControllerConfig) !==
        getCollectionIdFromConfig({ dataset: datasetConfigUpdates })

    const updatedDataset = defaults(
      {},
      datasetConfigUpdates,
      existingControllerConfig.dataset,
      defaultDatasetConfiguration,
    )

    const updatedControllerConfig = {
      ...existingControllerConfig,

      dataset: omitFalseProperties(
        wasCollectionChanged
          ? defaults({}, datasetConfigUpdates, defaultDatasetConfiguration)
          : updatedDataset,
      ),
    }

    const saveConfigPromise = match(controllerType)
      .with(DATASET, WIDGET_DATASET, () =>
        editorSdkProxy.controllers.saveConfiguration({
          controllerRef,
          config: updatedControllerConfig,
        }),
      )
      .with(ROUTER_DATASET, () =>
        saveRouterDatasetConfig(
          editorSdkProxy,
          controllerRef,
          updatedControllerConfig,
        ),
      )
      .exhaustive()

    const clearExistingCollectionsPromise = wasCollectionChanged
      ? clearAllControllerConnections({
          editorSdkProxy,
          controllerRef,
        })
      : Promise.resolve()

    if (apiConfig.filters) {
      await clearExistingCollectionsPromise.then(async () => {
        const actualFilterConnections = await fetchFilterConnections({
          editorSdkProxy,
          controllerRef,
        })
        return Promise.all([
          syncFilterConnections({
            editorSdkProxy,
            expectedConnections: filterConnectionsUpdates,
            actualConnections: actualFilterConnections,
          }),
        ])
      })
    }

    await Promise.all([saveConfigPromise, clearExistingCollectionsPromise])

    const appApi = await editorSdkProxy.editor.getAppAPI()
    await appApi.refreshControllersState({
      controllerRefs: [controllerRef],
    })

    if (withHistoryLabel) {
      await editorSdkProxy.history.add({
        label: UNDO_REDO_LABELS.SAVE_CONFIGURATION,
      })
    }

    const isRouterDataset = controllerType === ROUTER_DATASET

    if (
      !isEqual(
        get(existingControllerConfig, 'dataset.filter'),
        updatedControllerConfig.dataset.filter,
      )
    ) {
      await editorSdkProxy.editor.routers.refresh().catch(() => {}) // Editor X does not have implementation for it yet

      if (isRouterDataset) {
        const pageRef = await editorSdkProxy.components.getPage({
          componentRef: controllerRef,
        })
        appApi.emit('routerDatasetFilterChanged', pageRef)
      }
    }

    await editorSdkProxy.document.application.livePreview.refresh({
      source: 'DATASET_CONFIG_CHANGE',
      shouldFetchData: isRouterDataset,
    })
  }

  /**
   * UpdateDatasetWithCreateDatasetPanelResultParams type definition
   * @typedef {Object} UpdateDatasetWithCreateDatasetPanelResultParams
   * @property {*} controllerRef
   * @property {CreateDatasetPanelResult} createDatasetPanelResult
   */
  /**
   * CreateDatasetPanelResult type definition
   * @typedef {Object} CreateDatasetPanelResult
   * @property {string} collectionId
   * @property {string} displayName
   */

  /**
   * Update a Dataset config using user inputs from Create Dataset panel.
   * Used when pre-configuring a dataset added from Add Panel with defaults
   * @param {UpdateDatasetWithCreateDatasetPanelResultParams} params
   */
  const updateDatasetWithCreateDatasetPanelResult = async ({
    controllerRef,
    createDatasetPanelResult: { collectionId, displayName },
  }) => {
    const [collection, appApi] = await Promise.all([
      collectionsApi.get(collectionId),
      editorSdkProxy.editor.getAppAPI(),
    ])

    const pageSize = getDefaultPageSize({
      isSinglesCollection: isSinglesCollectionSchema(collection),
      maxPageSize: collection.maxPageSize,
    })

    const { config: existingControllerConfig } =
      await editorSdkProxy.controllers.getData({ controllerRef })

    const finalDatasetConfig = {
      ...defaultDatasetConfiguration,
      pageSize,
      ...existingControllerConfig?.dataset,
      collectionName: collectionId,
    }

    await Promise.all([
      editorSdkProxy.controllers.saveConfiguration({
        controllerRef,
        config: { dataset: omitFalseProperties(finalDatasetConfig) },
      }),
      editorSdkProxy.controllers.setDisplayName({
        controllerRef,
        name: displayName,
      }),
    ])

    await appApi.refreshControllersState({
      controllerRefs: [controllerRef],
    })
  }

  const setDatasetDisplayName = async (
    controllerRef,
    newDisplayName,
    omitHistoryLabel = false,
  ) => {
    await editorSdkProxy.controllers.setDisplayName({
      controllerRef,
      name: newDisplayName,
    })
    if (!omitHistoryLabel) {
      await editorSdkProxy.history.add({
        label: UNDO_REDO_LABELS.SET_DISPLAY_NAME,
      })
    }
  }

  /**
   * Fetch Dataset / RouterDataset's info & configurations
   * @param {*} controllerRef
   * @returns {DatasetInfo}
   */
  const fetchDataset = async controllerRef => {
    const [
      { type: controllerType, config: controllerConfig, displayName },
      pageRef,
    ] = await Promise.all([
      editorSdkProxy.controllers.getData({ controllerRef }),
      editorSdkProxy.components.getPage({
        componentRef: controllerRef,
      }),
    ])

    assertValidDatasetType(controllerType)

    const existingControllerConfig = await match(controllerType)
      .with(DATASET, WIDGET_DATASET, () => controllerConfig)
      .with(ROUTER_DATASET, () =>
        getRouterDatasetConfig(editorSdkProxy, controllerRef, controllerConfig),
      )
      .exhaustive()

    const filterConnections = await fetchFilterConnections({
      editorSdkProxy,
      controllerRef,
    })

    const apiDatasetConfig = toApiConfig(
      existingControllerConfig.dataset,
      filterConnections,
    )

    return {
      controllerRef,
      type: controllerType,
      displayName,
      config: apiDatasetConfig,
      isOnMasterPage: pageRef.id === 'masterPage',
    }
  }

  const fetchConnectableDatasets = async componentRef =>
    editorSdkProxy.controllers
      .listConnectableControllers({ componentRef })
      .then(connectableControllers =>
        Promise.all(
          connectableControllers.map(connectableController =>
            fetchDataset(connectableController.controllerRef).catch(() => null),
          ),
        ),
      )
      .then(connectableControllers => connectableControllers.filter(Boolean))

  const removeTableColumns = async ({ componentRef, controllerRef }) => {
    const collections = await fetchCollectionSchemas(collectionsApi)
    await syncPropsDerivedFromConnections({
      editorSdkProxy,
      controllerRef,
      componentRef,
      newColumns: [],
      collections,
    })
    await editorSdkProxy.components.properties.update({
      componentRef,
      props: { columns: [] },
    })
  }

  const saveTableColumns = ({ componentRef, newColumns }) =>
    editorSdkProxy.components.properties.update({
      componentRef,
      props: { columns: newColumns },
    })

  const connectBindingSection = async ({
    componentRef,
    controllerRef,
    newBindings,
    newColumns = [],
    role,
    requiresPrimaryConnection,
    isUserInputFilter,
  }) => {
    const [
      collections,
      repeaterAncestor,
      subRole,
      isPrimary,
      { controllerRef: previousControllerRef, bindings: oldBindings = [] },
    ] = await Promise.all([
      fetchCollectionSchemas(collectionsApi),
      getRepeaterAncestor({
        editorSdkProxy,
        componentRef,
      }),
      getSubrole({
        editorSdkProxy,
        componentRef,
        role,
        bindings: newBindings,
      }),
      requiresPrimaryConnection &&
        checkIfComponentHasNoOtherPrimaryConnection({
          editorSdkProxy,
          componentRef,
          role,
        }),
      fetchComponentBindings(componentRef).then(
        sections => sections.find(section => section.role === role) || {},
      ),
    ])

    const connectToNewController =
      !previousControllerRef || controllerRef.id !== previousControllerRef.id

    await syncPropsDerivedFromConnections({
      editorSdkProxy,
      controllerRef,
      componentRef,
      newBindings,
      newColumns,
      collections,
    })

    if (isUserInputFilter) {
      const [componentData, componentProperties] = await Promise.all([
        editorSdkProxy.components.data.get({
          componentRef,
        }),
        editorSdkProxy.components.properties.get({
          componentRef,
        }),
      ])

      const componentPropsAndDataUpdates = [
        await updateComponentLabelAndPlaceholder({
          collections,
          componentData,
          controllerRef,
          editorSdkProxy,
          i18n,
          newBindings,
        }),
        uncheckComponentsCheckedProperty({
          componentData,
        }),
        markComponentNonRequired({
          componentProperties,
        }),
        markComponentNonReadOnly({
          componentData,
        }),
        overrideRangeStepsDefaults({
          componentData,
        }),
      ]

      const { props, data } = merge({}, ...componentPropsAndDataUpdates)

      if (props) {
        await editorSdkProxy.components.properties.update({
          componentRef,
          props,
        })
      }
      if (data) {
        await editorSdkProxy.components.data.update({
          componentRef,
          data,
        })
      }
    }

    if (role === TEXT_ROLE) {
      await syncComponentBehaviorMetadata({
        editorSdkProxy,
        componentRef,
        newBindings,
        oldBindings,
      })
    } else if (role === GRID_ROLE) {
      await saveTableColumns({
        componentRef,
        newColumns,
      })
    }

    if (previousControllerRef) {
      if (connectToNewController) {
        await syncPropsDerivedFromConnections({
          editorSdkProxy,
          controllerRef: previousControllerRef,
          componentRef,
          newBindings: [],
          collections,
        })
      }

      await disconnectBinding({
        editorSdkProxy,
        controllerRef: previousControllerRef,
        componentRef,
        role,
      })
    }

    await editorSdkProxy.controllers.connect({
      controllerRef,
      role,
      subRole,
      connectToRef: componentRef,
      connectionConfig: createConnectionConfig(newBindings),
      isPrimary,
    })

    const updatedRepeaterAncestor = await connectRepeaterAncestorIfNeeded({
      editorSdkProxy,
      componentRef,
      repeaterAncestor,
      controllerRef,
    })

    await controllerConnectionChanged({
      editorSdkProxy,
      controllerRef,
    })

    return {
      repeaterAncestor: updatedRepeaterAncestor,
    }
  }

  const disconnectBindingSection = async ({
    componentRef,
    controllerRef,
    role,
  }) => {
    const collections = await fetchCollectionSchemas(collectionsApi)
    await syncPropsDerivedFromConnections({
      editorSdkProxy,
      controllerRef,
      componentRef,
      collections,
    })

    if (role === TEXT_ROLE) {
      await clearComponentBehaviorMetadata({
        editorSdkProxy,
        componentRef,
      })
    } else if (role === GRID_ROLE) {
      await removeTableColumns({ componentRef, controllerRef })
    }

    await disconnectBinding({
      editorSdkProxy,
      controllerRef,
      componentRef,
      role,
    })

    await controllerConnectionChanged({
      editorSdkProxy,
      controllerRef,
    })
  }

  const fetchComponentBindings = async componentRef =>
    editorSdkProxy.controllers
      .listConnections({ componentRef })
      .then(connections =>
        Promise.all(
          connections.map(async ({ controllerRef, role, config }) => {
            const data = await editorSdkProxy.controllers.getData({
              controllerRef,
            })

            return {
              controllerRef,
              role,
              config,
              controllerType: data?.type,
            }
          }),
        )
          .then(enrichedConnections =>
            Promise.all(
              enrichedConnections.map(
                async ({ controllerRef, role, config, controllerType }) => {
                  const bindings = await parseBindings(config)
                  return { controllerRef, role, bindings, controllerType }
                },
              ),
            ),
          )
          .then(connections =>
            connections.filter(({ controllerType }) =>
              [...ALL_DATASET_TYPES, undefined].includes(controllerType),
            ),
          ),
      )

  const fetchConnectedDataset = async componentRef =>
    editorSdkProxy.controllers
      .listConnections({ componentRef })
      .then(connections => {
        if (!connections.length) {
          return null
        }
        const { controllerRef } = connections[connections.length - 1]
        return fetchDataset(controllerRef)
      })

  const createDataset = async (
    pageRef,
    { displayName, datasetType = DATASET, ...initialConfig },
  ) => {
    if (
      ![DATASET, features.propsBinding && WIDGET_DATASET]
        .filter(Boolean)
        .includes(datasetType)
    ) {
      throw new Error(`Unsupported dataset type: ${datasetType}`)
    }

    const { datasetConfig } = fromApiConfig(
      initialConfig,
      undefined, // TODO: remove the need to pass controllerRef here
    )

    const controllerConfig = {
      dataset: omitFalseProperties(
        defaults({}, datasetConfig, defaultDatasetConfiguration),
      ),
    }

    const datasetComponentDefinition = getDatasetComponentDefinition({
      displayName,
      datasetType,
      config: controllerConfig,
      editorType,
      features,
    })

    const controllerRef = await editorSdkProxy.components.add({
      componentDefinition: datasetComponentDefinition,
      pageRef,
    })
    await editorSdkProxy.controllers.setState({
      state: { configured: [controllerRef] },
    })

    const apiDatasetConfig = toApiConfig(controllerConfig.dataset, [])

    const appApi = await editorSdkProxy.editor.getAppAPI()
    await appApi.emit('datasetAdded')

    return {
      controllerRef,
      type: datasetType,
      displayName,
      config: apiDatasetConfig,
      isOnMasterPage: pageRef.id === 'masterPage',
    }
  }

  const duplicateDataset = async (
    pageRef,
    { displayName, ...datasetConfig },
  ) => {
    const existingDatasetNames =
      await fetchExistingControllerNames(editorSdkProxy)
    const newDisplayName = generateUniqueName(
      (prefix, number) => `${prefix} ${number}`,
      displayName.replace(/ \d+$/, ''),
      existingDatasetNames,
      {
        allowNameWithoutSuffix: true,
        startIndex: 2,
      },
    )

    const omitUserInputFiltersV1 = config => ({
      ...config,
      filters: (config.filters || []).filter(
        filterDescriptor => !filterDescriptor.filterId,
      ),
    })

    const omitBindingRelatedConfigProps = config =>
      omit(config, ['nested', 'includes'])

    const normalizedConfig = omitUserInputFiltersV1(
      omitBindingRelatedConfigProps(datasetConfig),
    )

    return createDataset(pageRef, {
      ...normalizedConfig,
      displayName: newDisplayName,
    })
  }

  const fetchRepeaterChildrenBindings = async ({
    componentRef: repeaterRef,
    repeaterChildRef = null,
  }) => {
    const getComponentChildren = async componentRef => {
      const componentChildren = await editorSdkProxy.components.getChildren({
        componentRef,
      })

      return isEmpty(componentChildren)
        ? editorSdkProxy.components.getChildren({
            componentRef,
            fromDocument: true,
          })
        : componentChildren
    }

    const repeaterItemContainerRef = repeaterChildRef
      ? (
          await getRepeaterItemAncestor({
            editorSdkProxy,
            repeaterRef,
            componentRef: repeaterChildRef,
          })
        ).getOrElse(repeaterChildRef)
      : head(await getComponentChildren(repeaterRef))

    const containerChildren = await editorSdkProxy.components.getChildren({
      componentRef: repeaterItemContainerRef,
      recursive: true,
    })

    const childrenRefs = containerChildren.concat(repeaterItemContainerRef)

    const childStructures = await editorSdkProxy.components.get({
      componentRefs: childrenRefs,
      properties: ['sdkType', 'connections'],
    })

    return childStructures.map(({ componentRef, sdkType, connections }) => ({
      componentRef,
      componentType: sdkType,
      connections: connections.map(({ controllerRef, role, config }) => {
        const bindings = parseBindings(config)
        return { controllerRef, role, bindings }
      }),
    }))
  }

  return {
    setDatasetDisplayName,
    changePresetDatasetCollection,
    updateDatasetConfig: transactionClient.wrap(updateDatasetConfig),
    updateDatasetWithCreateDatasetPanelResult: transactionClient.wrap(
      updateDatasetWithCreateDatasetPanelResult,
    ),
    fetchDataset,
    fetchConnectableDatasets,
    connectBindingSection: transactionClient.wrap(connectBindingSection),
    disconnectBindingSection: transactionClient.wrap(disconnectBindingSection),
    fetchComponentBindings,
    fetchConnectedDataset,
    fetchRepeaterChildrenBindings,
    createDataset,
    duplicateDataset,
    saveTableColumns: transactionClient.wrap(saveTableColumns),
    removeTableColumns: transactionClient.wrap(removeTableColumns),
    addSubmitFeedbackMessage: ({ behaviorType, controllerRef, componentRef }) =>
      addSubmitFeedbackMessage({
        editorSdkProxy,
        behaviorType,
        controllerRef,
        componentRef,
        editorType,
      }),
  }
}

const assertValidDatasetType = type => {
  if (![DATASET, ROUTER_DATASET, WIDGET_DATASET].includes(type)) {
    throw new Error(`Unsupported controller type ${type}`)
  }
}
